<template>
  <div class="vcc-wrap">
    <!-- HEAD -->
    <div class="vcc-head">
      <div class="vcc-av">
        <viki-face :mood="mood" :speaking="speaking" :size="36" style="width:48px;height:36px;display:block;overflow:hidden;border-radius:0;"/>
      </div>
      <div class="vcc-info">
        <strong>Viki AI · Live Coach</strong>
        <span>● Watching {{ clientFirstName }}</span>
      </div>
      <div class="vcc-live">LIVE</div>
    </div>

    <!-- BODY -->
    <div class="vcc-body">

      <!-- Speech bubble -->
      <div class="vcc-bubble" :class="{ speaking: speaking }">
        <span v-html="bubbleText"></span>
      </div>

      <!-- Readiness -->
      <div class="vcc-rdy-row">
        <span>Client Readiness</span>
        <span class="vcc-rdy-val" :style="{ color: readinessColor }">{{ readiness }} / 100</span>
      </div>
      <div class="vcc-rdy-track">
        <div class="vcc-rdy-fill" :style="{ width: readiness + '%', background: readinessGradient }"></div>
      </div>

      <!-- Budget estimate -->
      <div class="vcc-budget-row">
        <div class="vcc-budget-lbl">Est. Budget Range</div>
        <div class="vcc-budget-val">{{ budgetRange }}</div>
      </div>

      <!-- Mini stats -->
      <div class="vcc-mini-stats">
        <div class="vcc-ms">
          <div class="vcc-ms-v">{{ callCount }}</div>
          <div class="vcc-ms-l">Prior Calls</div>
        </div>
        <div class="vcc-ms">
          <div class="vcc-ms-v vcc-ms-gold">{{ daysSince }}</div>
          <div class="vcc-ms-l">Since Reg</div>
        </div>
        <div class="vcc-ms">
          <div class="vcc-ms-v" :style="{ color: urgencyColor }">{{ urgencyLabel }}</div>
          <div class="vcc-ms-l">{{ urgencyType }}</div>
        </div>
      </div>

      <!-- Best agent match -->
      <div class="vcc-best-row" v-if="assignedAgentName">
        <div class="vcc-best-lbl">Assigned Agent</div>
        <div class="vcc-best-val">{{ assignedAgentName }}</div>
      </div>

      <!-- Campaign insight -->
      <div class="vcc-camp-row" v-if="client.keyword">
        <div class="vcc-camp-lbl">Campaign Signal</div>
        <div class="vcc-camp-val">{{ client.keyword }}</div>
      </div>

      <!-- Action button -->
      <button class="vcc-btn" @click.stop="$emit('call-now')">
        📞 Call Now · Open Script
      </button>

    </div>
  </div>
</template>

<script>
import VikiFace from './VikiFace.vue';
import moment from 'moment';

const BUBBLES_FRESH = [
  'Just registered. <strong>Call now</strong> before they cool off — first 24h converts 3× better.',
  '<strong>No calls yet</strong> on this lead. First touch window is still open. Draw first.',
  'Fresh lead from <strong>{kw}</strong>. That campaign converts well. Lead with the market angle.',
];
const BUBBLES_WARM = [
  'Called <strong>{calls}× before</strong> — not converted yet. Try a different opener today.',
  'This lead has been around <strong>{days} days</strong>. Show them something new.',
  '<strong>Re-engagement window</strong>: leads at this age close 40% better with a promo angle.',
];
const BUBBLES_HOT = [
  'This one <strong>deposited before</strong>. Upsell mode — they already trust the product.',
  '<strong>Active depositor</strong> — retention focus. Ask about their experience, not the pitch.',
  'Returning client signal. <strong>Warm opener</strong>, reference their history.',
];

export default {
  name: 'VikiCoachCard',
  components: { VikiFace },
  props: {
    client: { type: Object, required: true },
  },
  data() {
    return {
      speaking: false,
      mood: 'happy',
      speakInterval: null,
    };
  },
  computed: {
    clientFirstName() {
      return (this.client.name || '').split(' ')[0] || 'Client';
    },
    daysSince() {
      if (!this.client.created_at) return '?';
      const d = moment().diff(moment(this.client.created_at), 'days');
      return d === 0 ? 'Today' : d + 'd';
    },
    callCount() {
      // client.na is a HasMany relation — may be array or count
      const na = this.client.na;
      if (Array.isArray(na)) return na.length;
      return parseInt(na) || 0;
    },
    hasDeposit() {
      if (this.client.deposit_date || this.client.deposit_at) return true;
      if (Array.isArray(this.client.deposits) && this.client.deposits.length) return true;
      // Check if any deposit badge exists via the tag field
      return false;
    },
    readiness() {
      // Depositor always gets high readiness regardless of is_leading score
      if (this.hasDeposit) return 88;
      // is_leading from backend scoring (set at registration)
      const scored = parseInt(this.client.is_leading) || 0;
      if (scored > 0) return Math.min(100, scored);
      // Derive from recency + call count
      const days = parseInt(this.daysSince) || 0;
      const calls = this.callCount;
      if (days <= 1 && calls === 0) return 82;
      if (days <= 3 && calls === 0) return 72;
      if (days <= 7) return 58;
      if (days <= 30) return 42;
      return 28;
    },
    readinessColor() {
      if (this.readiness >= 75) return '#22c55e';
      if (this.readiness >= 50) return '#f59e0b';
      return '#ef4444';
    },
    readinessGradient() {
      if (this.readiness >= 75) return 'linear-gradient(90deg,#16a34a,#22c55e)';
      if (this.readiness >= 50) return 'linear-gradient(90deg,#d97706,#f59e0b)';
      return 'linear-gradient(90deg,#dc2626,#ef4444)';
    },
    budgetRange() {
      // Rough estimate based on country + deposit history
      if (this.hasDeposit) return '$5,000 – $20,000+';
      const days = parseInt(this.daysSince) || 99;
      if (days <= 2) return '$2,000 – $8,000';
      if (days <= 7) return '$1,000 – $5,000';
      return '$500 – $3,000';
    },
    urgencyLabel() {
      if (this.hasDeposit) return 'DEP';
      const days = parseInt(this.daysSince) || 99;
      if (days <= 1) return '🔥 HOT';
      if (days <= 3) return 'WARM';
      return 'COLD';
    },
    urgencyColor() {
      if (this.hasDeposit) return '#22c55e';
      const days = parseInt(this.daysSince) || 99;
      if (days <= 1) return '#ef4444';
      if (days <= 3) return '#f59e0b';
      return '#6b7280';
    },
    urgencyType() {
      return this.hasDeposit ? 'Status' : 'Temperature';
    },
    assignedAgentName() {
      // Try various shapes the CRM might return for the assigned agent
      if (this.client.assignment) {
        const a = this.client.assignment;
        if (a.assigned) {
          const u = a.assigned;
          return (u.name) || ((u.first_name || '') + ' ' + (u.last_name || '')).trim() || null;
        }
      }
      if (this.client.agent_name) return this.client.agent_name;
      if (this.client.agent) return typeof this.client.agent === 'string' ? this.client.agent : null;
      return null;
    },
    bubbleText() {
      const days = parseInt(this.daysSince) || 99;
      const kw = this.client.keyword || 'this campaign';
      let pool;
      if (this.hasDeposit) pool = BUBBLES_HOT;
      else if (days <= 3 && this.callCount === 0) pool = BUBBLES_FRESH;
      else pool = BUBBLES_WARM;
      const txt = pool[Math.floor(Date.now() / 8000) % pool.length];
      return txt.replace('{kw}', kw).replace('{calls}', this.callCount).replace('{days}', days);
    },
  },
  mounted() {
    this.speakInterval = setInterval(() => {
      this.speaking = true;
      setTimeout(() => { this.speaking = false; }, 2000);
    }, 5000);
    // Start with a speak
    setTimeout(() => { this.speaking = true; setTimeout(() => { this.speaking = false; }, 2200); }, 400);
  },
  beforeDestroy() {
    clearInterval(this.speakInterval);
  },
};
</script>

<style scoped>
.vcc-wrap {
  border-radius: 0 0 10px 10px;
  overflow: hidden;
  background: var(--c1-card);
  border-top: 1px solid var(--c1-brd-l);
}

/* HEAD */
.vcc-head {
  background: linear-gradient(135deg, #0f172a, #1e1a2e);
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid rgba(139,92,246,.18);
}
.vcc-av {
  width: 48px; height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg,#1e1a2e,#2e1a4e);
  border: 1px solid rgba(139,92,246,.35);
  overflow: hidden;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.vcc-info strong { display: block; font-size: 11px; font-weight: 700; color: #c4b5fd; }
.vcc-info span   { font-size: 10px; color: rgba(196,181,253,.5); }
.vcc-live {
  margin-left: auto;
  background: rgba(220,38,38,.85);
  border-radius: 4px;
  padding: 3px 7px;
  font-size: 9px; font-weight: 700; color: #fff;
  letter-spacing: 1px;
}

/* BODY */
.vcc-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 10px; }

/* BUBBLE */
.vcc-bubble {
  background: rgba(139,92,246,.07);
  border: 1px solid rgba(139,92,246,.18);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--c1-t2);
  transition: border-color .3s, background .3s;
}
.vcc-bubble.speaking {
  border-color: rgba(139,92,246,.45);
  background: rgba(139,92,246,.12);
}
.vcc-bubble :deep(strong) { color: #c4b5fd; }

/* READINESS */
.vcc-rdy-row {
  display: flex; justify-content: space-between;
  font-size: 10px; color: var(--c1-t3);
}
.vcc-rdy-val { font-weight: 700; }
.vcc-rdy-track {
  height: 6px;
  background: rgba(255,255,255,.06);
  border-radius: 3px;
  overflow: hidden;
}
.vcc-rdy-fill {
  height: 100%;
  border-radius: 3px;
  transition: width .8s ease;
}

/* BUDGET */
.vcc-budget-row {
  display: flex; justify-content: space-between; align-items: center;
  background: rgba(34,197,94,.06);
  border: 1px solid rgba(34,197,94,.14);
  border-radius: 7px;
  padding: 7px 11px;
}
.vcc-budget-lbl { font-size: 10px; color: var(--c1-t3); }
.vcc-budget-val { font-size: 13px; font-weight: 800; color: #22c55e; font-variant-numeric: tabular-nums; }

/* MINI STATS */
.vcc-mini-stats {
  display: grid; grid-template-columns: repeat(3,1fr); gap: 7px;
}
.vcc-ms {
  background: rgba(255,255,255,.04);
  border: 1px solid var(--c1-brd-l);
  border-radius: 7px;
  padding: 7px;
  text-align: center;
}
.vcc-ms-v { font-size: 14px; font-weight: 800; color: var(--c1-t1); font-variant-numeric: tabular-nums; }
.vcc-ms-l { font-size: 9px; color: var(--c1-t3); margin-top: 2px; }
.vcc-ms-gold { color: #f59e0b; }

/* BEST / CAMPAIGN ROW */
.vcc-best-row, .vcc-camp-row {
  display: flex; justify-content: space-between; align-items: center;
  background: rgba(21,144,176,.06);
  border: 1px solid rgba(21,144,176,.15);
  border-radius: 7px;
  padding: 7px 11px;
}
.vcc-best-lbl, .vcc-camp-lbl { font-size: 10px; color: var(--c1-t3); }
.vcc-best-val { font-size: 12px; font-weight: 700; color: #06b6d4; }
.vcc-camp-val { font-size: 12px; font-weight: 700; color: #60a5fa; }

/* ACTION BUTTON */
.vcc-btn {
  width: 100%;
  background: linear-gradient(135deg, #7c3aed, #8b5cf6);
  border: none;
  border-radius: 8px;
  padding: 11px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  letter-spacing: .3px;
  transition: opacity .15s, transform .1s;
  box-shadow: 0 4px 14px rgba(124,58,237,.35);
}
.vcc-btn:hover { opacity: .9; transform: translateY(-1px); }
.vcc-btn:active { transform: translateY(0); }
</style>
